import React, { useState } from 'react';
import { useRetentionMetrics } from './hooks/useRetentionMetrics';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { exportRetentionMetrics } from '../../../../utils/exportToExcel';
import { IconDownload } from '@tabler/icons-react';

const SEGMENT_LABELS = {
  'power_user': 'Power (20+ checks/week)',
  'heavy': 'Heavy (10-19 checks/week)',
  'medium': 'Medium (3-9 checks/week)',
  'light': 'Light (<3 checks/week)'
};

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b'];

const RetentionGrowth = () => {
  const [dateRange, setDateRange] = useState('30d');
  const { data, loading, error, refetch } = useRetentionMetrics({ dateRange, organization: null });

  // Calculate average retention rates
  const avg7dRetention = data.retention7d?.retention_data?.length > 0
    ? (data.retention7d.retention_data.reduce((sum, item) => sum + item.retention_rate, 0) / data.retention7d.retention_data.length).toFixed(1)
    : '--';

  const avg30dRetention = data.retention30d?.retention_data?.length > 0
    ? (data.retention30d.retention_data.reduce((sum, item) => sum + item.retention_rate, 0) / data.retention30d.retention_data.length).toFixed(1)
    : '--';

  const latestStickiness = data.dauMauStickiness?.metrics_data?.length > 0
    ? data.dauMauStickiness.metrics_data[0].stickiness.toFixed(1)
    : '--';

  // Helper function for safe date formatting
  const formatDate = (dateStr) => {
    try {
      if (dateStr) {
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
        }
      }
    } catch (e) {
      console.error('Error parsing date:', dateStr, e);
    }
    return 'Invalid Date';
  };

  // Prepare chart data
  const dauMauChartData = data.dauMauStickiness?.metrics_data
    ? [...data.dauMauStickiness.metrics_data].reverse().map(item => ({
        date: formatDate(item.date),
        DAU: item.dau,
        MAU: item.mau,
        'Stickiness %': item.stickiness
      }))
    : [];

  const powerUserPieData = data.powerUserSegmentation?.segmentation_data
    ? data.powerUserSegmentation.segmentation_data.map(item => ({
        name: SEGMENT_LABELS[item.segment] || item.segment,
        value: item.user_count,
        percentage: item.percentage
      }))
    : [];

  return (
    <div className="p-6 space-y-6">
      <div className="border-b pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Retention & Growth</h1>
        <p className="text-gray-600 mt-2">
          Track user retention, engagement metrics, and power user segmentation
        </p>
      </div>

      {/* Date Range Filter */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => exportRetentionMetrics(data, dateRange)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-[#2563eb] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <IconDownload size={18} />
          Export to Excel
        </button>
        <select 
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="border border-gray-300 rounded-md px-4 py-2 bg-white text-sm"
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </select>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center justify-between">
          <span>Error loading data: {error.message}</span>
          <button
            onClick={refetch}
            className="ml-4 px-3 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded text-sm font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Metric Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">7-Day Retention</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '...' : `${avg7dRetention}%`}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Loading...' : `Avg of ${data.retention7d?.retention_data?.length || 0} cohorts`}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">30-Day Retention</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '...' : `${avg30dRetention}%`}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Loading...' : `Avg of ${data.retention30d?.retention_data?.length || 0} cohorts`}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">DAU/MAU Stickiness</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '...' : `${latestStickiness}%`}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Loading...' : 'Most recent day'}
          </p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Cohort Retention (7-Day)</h3>
          {loading ? (
            <div className="h-64 flex items-center justify-center text-gray-400">
              Loading cohort data...
            </div>
          ) : data.retention7d?.retention_data?.length > 0 ? (
            <div className="h-64 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Cohort Date</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Size</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Retained</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.retention7d.retention_data.map((cohort, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2">{cohort.cohort_date}</td>
                      <td className="px-3 py-2 text-right">{cohort.cohort_size}</td>
                      <td className="px-3 py-2 text-right">{cohort.retained_users}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`font-medium ${cohort.retention_rate > 50 ? 'text-green-600' : 'text-gray-600'}`}>
                          {cohort.retention_rate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
              <p>No cohort data available</p>
              <p className="text-sm mt-2">Try a longer date range</p>
            </div>
          )}
        </div>
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">DAU/MAU Trends</h3>
          {loading ? (
            <div className="h-64 flex items-center justify-center text-gray-400">
              Loading trends...
            </div>
          ) : dauMauChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={dauMauChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="DAU" stroke="#3b82f6" strokeWidth={2} />
                <Line type="monotone" dataKey="MAU" stroke="#8b5cf6" strokeWidth={2} />
                <Line type="monotone" dataKey="Stickiness %" stroke="#ec4899" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
              <p>No trend data available</p>
              <p className="text-sm mt-2">Data will appear as usage grows</p>
            </div>
          )}
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Power User Segmentation</h3>
          {loading ? (
            <div className="h-64 flex items-center justify-center text-gray-400">
              Loading segmentation...
            </div>
          ) : powerUserPieData.length > 0 ? (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={powerUserPieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.percentage.toFixed(1)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {powerUserPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 text-sm text-gray-600">
                Total Users: {data.powerUserSegmentation?.total_users || 0}
              </div>
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
              <p>No segmentation data available</p>
              <p className="text-sm mt-2">Requires user activity data</p>
            </div>
          )}
        </div>
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Practice Retention</h3>
          {loading ? (
            <div className="h-64 flex items-center justify-center text-gray-400">
              Loading practice retention...
            </div>
          ) : data.practiceRetention?.retention_data?.length > 0 ? (
            <div className="h-64 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Cohort Date</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Total</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Retained</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.practiceRetention.retention_data.map((cohort, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2">{cohort.cohort_date}</td>
                      <td className="px-3 py-2 text-right">{cohort.total_practices}</td>
                      <td className="px-3 py-2 text-right">{cohort.retained_practices}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`font-medium ${cohort.retention_rate > 80 ? 'text-green-600' : 'text-gray-600'}`}>
                          {cohort.retention_rate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
              <p>No practice retention data</p>
              <p className="text-sm mt-2">Data will appear as practices join</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetentionGrowth;
